'use client';

import { c as _c } from "react/compiler-runtime";
import { PAGE_DATA_NUMBER_CLASS } from '@/domains/pages/shared/typography';
import { cn } from '@/utils/shad';
import { Fragment, type ReactNode } from 'react';
export type CompositionLegendItem = {
  key: string;
  name: string;
  value: number;
  share?: number;
  color: string;
};
type CompositionLegendProps = {
  items: CompositionLegendItem[];
  hoveredKey: string | null;
  onHoverChange: (key: string | null) => void;
  formatValue: (item: CompositionLegendItem) => ReactNode;
  className?: string;
};

/** A long segment name gets an ellipsis rather than pushing the rest of the list off the row. */
const LEGEND_NAME_MAX_WIDTH_CLASS = 'max-w-45';
export function CompositionLegend(t0) {
  const $ = _c(14);
  const {
    items,
    hoveredKey,
    onHoverChange,
    formatValue,
    className
  } = t0;
  if (items.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cn("no-scrollbar flex flex-nowrap items-center gap-1.5 overflow-x-auto text-xs leading-relaxed whitespace-nowrap text-muted-foreground", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== formatValue || $[3] !== hoveredKey || $[4] !== items || $[5] !== onHoverChange) {
    let t3;
    if ($[7] !== formatValue || $[8] !== hoveredKey || $[9] !== onHoverChange) {
      t3 = (item, index) => {
        const isHovered = hoveredKey === item.key;
        const isDimmed = hoveredKey != null && !isHovered;
        return <Fragment key={item.key}>{index > 0 ? <span className="shrink-0" aria-hidden={true}>·</span> : null}<button type="button" title={item.name} className={cn("flex shrink-0 cursor-pointer items-center gap-1 transition-opacity hover:text-foreground", isHovered && "text-foreground", isDimmed && "opacity-35")} onMouseEnter={() => onHoverChange(item.key)} onMouseLeave={() => onHoverChange(null)} onFocus={() => onHoverChange(item.key)} onBlur={() => onHoverChange(null)}><span className="size-2 shrink-0 rounded-[2px]" style={{
              backgroundColor: item.color
            }} aria-hidden={true} /><span className={cn("truncate", LEGEND_NAME_MAX_WIDTH_CLASS)}>{item.name}</span><span className={cn("shrink-0 font-medium", PAGE_DATA_NUMBER_CLASS)}>{formatValue(item)}</span></button></Fragment>;
      };
      $[7] = formatValue;
      $[8] = hoveredKey;
      $[9] = onHoverChange;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    t2 = items.map(t3);
    $[2] = formatValue;
    $[3] = hoveredKey;
    $[4] = items;
    $[5] = onHoverChange;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[11] !== t1 || $[12] !== t2) {
    t3 = <div className={t1} data-testid="composition-legend">{t2}</div>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}